import type { ToolUseItem } from './harness.types';
import { isRecord } from './parse-utils';

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  return isRecord(value) ? value : undefined;
};

/** Plain text carried by a harness tool result, including chunked text blocks. */
export const readToolResultText = (item: ToolUseItem): string | undefined => {
  const content = item.result?.content;
  if (typeof content === 'string') return content || undefined;
  if (!Array.isArray(content)) return undefined;

  const text = content
    .map((part) => {
      const record = asRecord(part);
      return typeof record?.text === 'string' ? record.text : '';
    })
    .join('');
  return text || undefined;
};

const parseJsonRecord = (
  text: string | undefined
): Record<string, unknown> | undefined => {
  const trimmed = text?.trim();
  if (!trimmed) return undefined;

  const parse = (value: string): Record<string, unknown> | undefined => {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return undefined;
    }
  };

  const whole = parse(trimmed);
  if (whole) return whole;

  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  return start === -1 || end <= start ? undefined : parse(trimmed.slice(start, end + 1));
};

const appendRecordAndNestedData = (
  candidates: Record<string, unknown>[],
  value: unknown
): void => {
  const record = asRecord(value);
  if (!record) return;
  candidates.push(record);

  const data = asRecord(record.data);
  if (data) candidates.push(data);
};

/**
 * Structured tool-result payloads in authority order: live provider metadata,
 * structured content, then persisted JSON text. A `{ data: ... }` envelope is
 * unwrapped beside each source.
 */
export const readToolResultJsonCandidates = (
  item: ToolUseItem
): Record<string, unknown>[] => {
  const candidates: Record<string, unknown>[] = [];
  appendRecordAndNestedData(candidates, item.result_provider_metadata?.data);
  appendRecordAndNestedData(candidates, item.result?.content);
  appendRecordAndNestedData(candidates, parseJsonRecord(readToolResultText(item)));
  return candidates;
};
